import { computed } from 'vue'
import { useCompanyContext } from '@/composables/useCompanyContext'
import { useWorkerTerm } from '@/composables/useWorkerTerm'
import { useAuthStore } from '@/stores/auth'

/**
 * Company Flow — one place a screen asks "what is this called here?" and
 * "does this company show this field?".
 *
 * The answers come from the per-company context (/domains/company-context/),
 * which the backend resolves as: domain override -> preset default (e.g.
 * cleaning) -> system default. A company with no preset and no overrides gets
 * the system defaults below, so nothing an existing company sees changes until
 * that company is given a preset or an override.
 *
 * Terminology:
 *   const { term, termPlural, termLower } = useFlow()
 *   term('worker')          // "Cleaner" for a cleaning company, else "Technician"
 *   term('job', true)       // plural
 *
 * Fields / sections:
 *   const { field } = useFlow()
 *   v-if="field('section.appliances')"   // hidden for cleaning companies
 *
 * These are functions (not refs) so they stay reactive when read in a template:
 * they read the shared context ref, which repaints the bindings when it loads
 * or is patched after a save.
 */

// System defaults — mirror flow.TERM_SPEC on the backend so a screen shows the
// right generic wording instantly, before the context request resolves, and if
// it ever fails.
// Keep in lockstep with flow.TERM_SPEC on the backend. A key missing here makes
// term() fall back to the raw key string, so every registry key must be listed.
const TERM_DEFAULTS = {
  worker: ['Technician', 'Technicians'],
  customer: ['Customer', 'Customers'],
  employee: ['Employee', 'Employees'],
  job: ['Job', 'Jobs'],
  visit: ['Visit', 'Visits'],
  task: ['Task', 'Tasks'],
  estimate: ['Estimate', 'Estimates'],
  invoice: ['Invoice', 'Invoices'],
  service_line: ['Service Line', 'Service Lines'],
  payment: ['Payment', 'Payments'],
  price_book: ['Price Book', 'Price Books'],
  appliance: ['Appliance', 'Appliances'],
  brand: ['Brand', 'Brands'],
  part: ['Part', 'Parts'],
  zone: ['Zone', 'Zones'],
  club: ['TV Club', 'TV Clubs'],
}

// Preset terminology — mirror flow.PRESET_TERMS on the backend so a
// flow-gated label (e.g. the customer-card club tab) reads "Club" on first
// paint for a cleaning company, before the async company context resolves.
const PRESET_TERM_DEFAULTS = {
  cleaning: {
    club: ['Club', 'Clubs'],
    // Cleaning companies service cleanings, not appliances — mirror
    // flow.PRESET_TERMS so term('appliance') reads "Cleaning"/"Cleanings"
    // on first paint, before the async company context resolves.
    appliance: ['Cleaning', 'Cleanings'],
  },
}

// System defaults for optional fields/sections — mirror flow.FIELD_SPEC. All
// shown by default (generic appliance-repair company).
const FIELD_DEFAULTS = {
  'field.technician.license': true,
  'field.contact.second_phone': true,
  'section.appliances': true,
  'field.appliance_brands': true,
  'section.inventory': true,
  'menu.inventory_parts': true,
  'finance.parts': true,
  'menu.fleet': false,
}

// Preset field defaults — mirror flow.PRESET_FIELDS on the backend. Resolved
// synchronously from the auth store's preset so a flow-gated block (e.g. the
// Inventory & Parts menu) is hidden immediately on load for a cleaning company,
// instead of flashing in and then disappearing once the async company context
// resolves (TC-68). A company with no preset is unaffected.
const PRESET_FIELD_DEFAULTS = {
  cleaning: {
    'field.technician.license': false,
    'field.contact.second_phone': false,
    'section.appliances': false,
    'field.appliance_brands': false,
    'section.inventory': false,
    'menu.inventory_parts': false,
    'finance.parts': false,
    'menu.fleet': true,
  },
}

// Trade / vertical icons. The generic (appliance-repair) company keeps the
// wrench + hard-hat it has today; a vertical preset swaps them so the UI reads
// as that trade (a cleaning company sees a broom, not a wrench). Keyed by a
// stable role, resolved per preset so no screen hardcodes an industry icon.
//   icon('worker')  — the person who does the work (menu, badges, presets)
//   icon('trade')   — the trade/service glyph (service line, skills)
const ICON_DEFAULTS = {
  worker: 'mdi-account-hard-hat',
  trade: 'mdi-wrench',
}
const PRESET_ICONS = {
  cleaning: {
    worker: 'mdi-broom',
    trade: 'mdi-broom',
  },
}

/** Resolve a Company Flow field outside Vue setup (router guards, etc.). */
export function resolveFlowField(key, context, presetCode) {
  const cfg = context?.field_config?.[key]
  if (cfg && typeof cfg.shown === 'boolean') return cfg.shown
  const presetVal = presetCode && PRESET_FIELD_DEFAULTS[presetCode]?.[key]
  if (typeof presetVal === 'boolean') return presetVal
  return FIELD_DEFAULTS[key] !== false
}

export function useFlow() {
  const { context, load } = useCompanyContext()
  const { term: workerTerm, termPlural: workerTermPlural } = useWorkerTerm()
  const auth = useAuthStore()

  // Fire-and-forget: make sure the context is being fetched. Screens that
  // already loaded it (dispatch, New Job) pay nothing; the first caller warms
  // the shared cache for the rest.
  load()

  function clubBrandDefault(plural) {
    const d = auth.domain
    if (!d) return null
    if ((d.code || '').toLowerCase() === 'techvill') return null
    const brand = (d.legal_name || '').trim() || (d.name || '').trim()
    if (!brand) return null
    return plural ? `${brand} Clubs` : `${brand} Club`
  }

  function rawTerm(key, plural) {
    const idx = plural ? 'plural' : 'singular'
    const t = context.value?.terminology?.[key]
    if (t && t[idx]) return t[idx]
    // The worker term is also served synchronously by the auth store, so it is
    // correct immediately even before the context request resolves.
    if (key === 'worker') return plural ? workerTermPlural.value : workerTerm.value
    if (key === 'club') {
      const brandClub = clubBrandDefault(plural)
      if (brandClub) return brandClub
    }
    const p = context.value?.industry_preset_code || auth.domain?.industry_preset_code || null
    const presetDef = p && PRESET_TERM_DEFAULTS[p] && PRESET_TERM_DEFAULTS[p][key]
    if (presetDef) return plural ? presetDef[1] : presetDef[0]
    const def = TERM_DEFAULTS[key]
    if (def) return plural ? def[1] : def[0]
    return key
  }

  function term(key, plural = false) {
    return rawTerm(key, plural)
  }
  function termPlural(key) {
    return rawTerm(key, true)
  }
  function termLower(key) {
    return rawTerm(key, false).toLowerCase()
  }
  function termPluralLower(key) {
    return rawTerm(key, true).toLowerCase()
  }

  function field(key) {
    const p = context.value?.industry_preset_code || auth.domain?.industry_preset_code || null
    return resolveFlowField(key, context.value, p)
  }

  // Convenience: is this company on a given industry preset? Prefer the
  // company context, but fall back to the auth store's domain (populated
  // synchronously at login/refresh) so callers that read at setup time — the
  // main-nav menu is a plain array built once — still get the right preset
  // before the async context request resolves.
  const preset = computed(() =>
    context.value?.industry_preset_code || auth.domain?.industry_preset_code || null,
  )
  const isCleaning = computed(() => preset.value === 'cleaning')

  // Preset-aware trade icon. Falls back to the generic default, so a company
  // with no preset is byte-for-byte unchanged.
  function icon(key) {
    const p = preset.value
    const presetIcon = p && PRESET_ICONS[p] && PRESET_ICONS[p][key]
    if (presetIcon) return presetIcon
    return ICON_DEFAULTS[key] || key
  }

  return {
    context,
    term,
    termPlural,
    termLower,
    termPluralLower,
    field,
    icon,
    preset,
    isCleaning,
  }
}

export default useFlow
